import * as React from "react"
import { BeautifulMentionsPlugin } from "lexical-beautiful-mentions"
import { VariableOptions } from "platform-common/util"
import { Menu } from "./Menu"
import { MenuItem } from "./MenuItem"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { useDebouncedCallback } from "@react-hookz/web"
import { Popper } from "@mui/material"
import { VariableEditor } from "../VariableEditor/VariableEditor"
import { $createTextNode, $getSelection, $isNodeSelection, $getNearestNodeFromDOMNode } from "lexical"

import styles from "./VariablesPlugin.module.scss"

export type EmailTemplateValues = {
  "first name"?: string
  "last name"?: string
  company?: string[]
}

interface Props {
  /**
   * If true - user can insert variables into editor by starting to type the "{{" trigger.
   */
  allowCreateVariables: boolean

  /**
   * If true - clicking a variable opens a floating editor to replace it with provided text.
   */
  allowSetVariableValues?: boolean

  /**
   * Each time this changes, plugin will try to auto-fill variables
   * with names that match `variableValues` keys with according values.
   */
  variableValues?: Record<string, string>

  /**
   * If true - user can create custom variables by typing the name.
   */
  allowCustomVariables?: boolean

  /**
   * If provided - will be used to filter the variable options.
   */
  variableOptionFilter?(variableOption: string): boolean

  rootEditorElement: HTMLDivElement | null
}

export const VariablesPlugin = ({
  allowCreateVariables,
  allowSetVariableValues,
  variableValues,
  rootEditorElement,
  allowCustomVariables,
  variableOptionFilter,
}: Props) => {
  const [editor] = useLexicalComposerContext()
  const [variableEditorPopupRoot, setVariableEditorPopupRoot] = React.useState<HTMLElement | null>(null)

  /**
   * Return default options that match the input, other provide a custom option with user input
   */
  const handleFilterChange = React.useCallback(
    (_: unknown, query?: string | null) => {
      const normalized = query?.trim().toLowerCase().replace(/}+$/g, "") || ""
      const filtered = VariableOptions.map((o) => o.value)
        .filter((o) => o != "other}}" && o.includes(normalized))
        .filter((option) => (variableOptionFilter ? variableOptionFilter(option) : true))

      return Promise.resolve(filtered.length ? filtered : allowCustomVariables ? [`${normalized}}}`] : [])
    },
    [variableOptionFilter, allowCustomVariables]
  )

  const storePopupRoot = (e: MouseEvent) => setVariableEditorPopupRoot(e.target as HTMLElement)

  const handleDoneEditingVariableValue = (text: string) => {
    setVariableEditorPopupRoot(null)

    if (!text.trim()) return

    editor.update(() => {
      const variableNode = $getNearestNodeFromDOMNode(variableEditorPopupRoot as HTMLElement)
      variableNode?.replace($createTextNode(text))
    })
  }

  const handleEditorUpdate = useDebouncedCallback(
    () => {
      if (!allowSetVariableValues) return

      const variables = (editor._rootElement?.querySelectorAll("[data-beautiful-mention]") || []) as HTMLElement[]

      // Reset the click listener on each variable. On click - open the variable editor near the anchor.
      variables.forEach((el) => {
        el.removeEventListener("click", storePopupRoot)
        el.addEventListener("click", storePopupRoot)
      })
    },
    [editor, allowSetVariableValues, storePopupRoot],
    500
  )

  /**
   * Register editor state listener
   */
  React.useEffect(() => editor.registerUpdateListener(handleEditorUpdate), [editor, handleEditorUpdate])

  /**
   * Each time `variableValues` changes - try to auto-fill variable values
   */
  React.useEffect(() => {
    if (!variableValues) return

    const variables = (rootEditorElement?.querySelectorAll("[data-beautiful-mention]") || []) as HTMLElement[]

    for (const variable of variables) {
      // Focus on the variable and immediately close the editor popup
      variable.click()
      setVariableEditorPopupRoot(null)

      editor.update(() => {
        const selection = $getSelection()

        if (!$isNodeSelection(selection)) return

        // If `variableValues` has a value for current variable - replace the variable with value text
        selection.getNodes().forEach((node) => {
          const key = node.getTextContent().replace(/{{|}}/g, "")

          if (variableValues[key]) {
            node.replace($createTextNode(variableValues[key]))
          }
        })
      })
    }
  }, [variableValues, editor, rootEditorElement])

  return (
    <div>
      <BeautifulMentionsPlugin
        menuAnchorClassName={styles.menuAnchor}
        onSearch={handleFilterChange}
        triggers={["{{"]}
        searchDelay={0}
        menuItemComponent={MenuItem}
        menuComponent={allowCreateVariables ? Menu : () => null}
      />
      <Popper
        open={!!variableEditorPopupRoot}
        anchorEl={variableEditorPopupRoot}
        className={styles.variableEditorPopper}
      >
        <VariableEditor onDone={handleDoneEditingVariableValue} />
      </Popper>
    </div>
  )
}
